/**
 * The public tracking API — the only file app code needs to read.
 *
 * Every entry point is a no-op when there is no valid measurement id, when there
 * is no browser (unit tests / SSR), or when the tag failed to load. Analytics
 * must never throw into app code, and above all must never stand between
 * somebody and signing in.
 *
 * Two settings must match on the GA4 property side, because no client code can
 * override them:
 *  - Enhanced measurement's "page changes based on browser history events" must
 *    be OFF. It reports `location.href` verbatim, which bypasses the sanitizing
 *    in `sanitize-path.ts` and would record set-password and unlock tokens.
 *  - Enhanced measurement's "site search" must be OFF, for the same reason.
 */
import { environment } from 'src/environments/environment';

import { DEFAULT_LOAD_TIMEOUT_MS } from './analytics.constants';
import type {
  AnalyticsConfig,
  AnalyticsEventName,
  AnalyticsEventParams,
} from './analytics.types';
import { createGtagClient, type GtagClient, type GtagWindow } from './gtag-client';
import { resolveMeasurementId } from './measurement-id';
import { normalizeKey, redactText, scrubParams } from './redact';
import { sanitizeAbsoluteUrl, sanitizeAnalyticsPath } from './sanitize-path';

let initialized = false;
let client: GtagClient | null = null;
let allowedQueryParams: readonly string[] = [];
/** Previous sanitized location, sent as `page_referrer` so navigation flows link up. */
let lastPageLocation: string | null = null;

/** The browser window, or null under unit tests / any non-browser host. */
function getWindow(): GtagWindow | null {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return null;
  }
  return window as GtagWindow;
}

/**
 * Prepare the queue and configure the property. Idempotent, and called
 * automatically by the first `track*` call — pass a config explicitly only to
 * override the defaults taken from `environment`.
 */
export function initAnalytics(config: AnalyticsConfig = {}): void {
  if (initialized) return;

  const win = getWindow();
  if (!win) return;

  initialized = true;

  if (config.enabled === false) return;
  if (config.respectDoNotTrack === true && win.navigator?.doNotTrack === '1') {
    return;
  }

  const measurementId = resolveMeasurementId(
    config.measurementId ?? environment.gaMeasurementId,
  );
  if (!measurementId) return;

  allowedQueryParams = config.allowedQueryParams ?? [];
  client = createGtagClient(win, {
    measurementId,
    loadTimeoutMs: config.loadTimeoutMs ?? DEFAULT_LOAD_TIMEOUT_MS,
  });

  const hostname = win.location?.hostname ?? '';
  const referrer = sanitizeAbsoluteUrl(win.document.referrer);

  client.command('js', new Date());
  client.command('config', measurementId, {
    // Page views are sent by hand from sanitized paths; the automatic one would
    // report the raw URL.
    send_page_view: false,
    // Whatever is sent before the first navigation completes is still
    // attributed to a clean location rather than to `location.href`.
    page_location: `${win.location.origin}${sanitizeAnalyticsPath(
      win.location.pathname,
      allowedQueryParams,
    )}`,
    ...(referrer ? { page_referrer: referrer } : {}),
    app_version: environment.appVersion,
    debug_mode: !environment.production,
    cookie_domain: config.cookieDomain === 'auto' ? 'auto' : hostname,
  });
}

/**
 * The client to send through, or null when tracking is off. Also triggers the
 * lazy tag load, so the fetch happens on first use rather than at bootstrap.
 */
function activeClient(): GtagClient | null {
  if (!initialized) initAnalytics();
  if (!client || client.hasFailed) return null;
  client.ensureLoaded();
  return client;
}

/**
 * Send an event. The name and the parameter shape both come from
 * `AnalyticsEventParams`, so a new event is added there first.
 */
export function trackEvent<N extends AnalyticsEventName>(
  name: N,
  params: AnalyticsEventParams[N],
): void {
  const active = activeClient();
  if (!active) return;

  const eventName = normalizeKey(name);
  if (!eventName) return;

  active.command(
    'event',
    eventName as AnalyticsEventName,
    scrubParams(params as Readonly<Record<string, unknown>>),
  );
}

/**
 * Send a page view for an Angular navigation. Pass `router.url`.
 *
 * The sanitized location is also made the page every later event is
 * attributed to. Without that, gtag stamps each event with `location.href`,
 * and an event fired on a set-password page would carry its token.
 *
 * A navigation that changes only the query string is not a new page: the
 * security page rewrites its own query to drop a spent ticket, and counting
 * that would double every visit to it.
 */
export function trackPageView(url: string, title?: string): void {
  const win = getWindow();
  const active = activeClient();
  if (!win || !active) return;

  const path = sanitizeAnalyticsPath(url, allowedQueryParams);
  const location = `${win.location.origin}${path}`;
  if (location === lastPageLocation) return;

  const page = {
    page_location: location,
    page_title: redactText(title ?? win.document.title ?? ''),
    ...(lastPageLocation ? { page_referrer: lastPageLocation } : {}),
  };

  active.command('set', page);
  trackEvent('page_view', page);

  lastPageLocation = location;
}

/**
 * An application error worth counting. The description is redacted like any
 * other value, since error text often quotes the data that caused it.
 */
export function trackAppError(
  description: string,
  context: Omit<AnalyticsEventParams['app_error'], 'error_description'> = {},
): void {
  trackEvent('app_error', {
    ...context,
    error_description: redactText(description) ?? 'unknown',
  });
}

/**
 * Drop all module state. Intended for tests — production code has no reason to
 * re-init, and this does not remove an already-injected tag.
 */
export function resetAnalytics(): void {
  initialized = false;
  client = null;
  allowedQueryParams = [];
  lastPageLocation = null;
}
